import useMedRequestFiltering from '@/lib/api/med-requests/filtering'
import usePatientOrderHistory from '@/lib/api/med-requests/order-history'
import { useGetInvestigations } from '@/services/investigations/useGetInvestigations'
import { useInvestigationRequestById, useMedicationRequestById } from '@/services/medicationRequests'
import { useLockItemById } from '@/services/useLockItem'
import { useParams } from 'react-router-dom'
import type { Investigation, MedRequest } from '@/types/data'
import { shouldLockInvestigation } from '@/lib/permissions'
import { familyKeyOf, isMultiProductOrderAwaitingItems, shouldLockOpenedMedRequest } from '@/lib/med-requests'
import { useScrAiSummary } from './scr-ai-summary'
import { useConsultationAISummary } from './useConsultationAISummary'
import useGetConversationById from './useGetConversationById'
import { useGetMedications } from '@/services/medications'
import { useQueryClient } from '@tanstack/react-query'
import { useFilterStore } from '@/lib/stores/filter'
import { useFlag } from '@unleash/proxy-client-react'
import { isMultiProductOrderMedRequest } from '@/lib/utils'

const usePrepareNextRequest = (enabled = true, isInvestigation = false) => {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const isOrderNumberSearch = useFilterStore(state => state.filteringOptions['Order Number'].length > 0)
  const isMultiProductOrdersEnabled = useFlag('multi-product-orders')

  const { data: medRequests } = useMedRequestFiltering(!isInvestigation)
  const { data: investigations } = useGetInvestigations(
    {},
    {
      enabled: enabled && isInvestigation
    }
  )

  // Cache read only: the page loads the opened request itself.
  const { data: currentMedRequest } = useMedicationRequestById(id ?? '', false)
  // Siblings are decided with the opened request, and once it's decided one may head the queue.
  const currentFamilyKey = !isInvestigation && currentMedRequest ? familyKeyOf(currentMedRequest) : null
  const isCurrentItem = (item: Investigation | MedRequest) =>
    item.id === id || (!!currentFamilyKey && familyKeyOf(item as MedRequest) === currentFamilyKey)

  const items = isInvestigation ? investigations : medRequests?.data
  const nextIndex = (items?.findIndex(e => e?.id === id) ?? -1) + 1
  // Skipped once its prefetch lands (only that shows the order's items); a search still opens an
  // awaiting order. A skipped entry isn't refetched, so it may stay skipped until it's collected (gcTime).
  const isSkippedOrder = (item: Investigation | MedRequest) => {
    if (isInvestigation) return false

    const prefetched = queryClient.getQueryData<MedRequest>(['medicationRequestById', item.id])
    if (!isMultiProductOrdersEnabled) return isMultiProductOrderMedRequest(prefetched)
    return !isOrderNumberSearch && isMultiProductOrderAwaitingItems(prefetched)
  }
  const nextItem =
    items &&
    [...items.slice(nextIndex), ...items.slice(0, nextIndex)].find(e => !isCurrentItem(e) && !isSkippedOrder(e))

  const investigation = useInvestigationRequestById(nextItem?.id ?? '', !!nextItem && enabled && isInvestigation)
  const medRequest = useMedicationRequestById(
    (isInvestigation ? investigation?.data?.medicationRequestId : nextItem?.id) ?? '',
    ((isInvestigation && !!investigation?.data) || !!nextItem) && enabled
  )

  useGetConversationById({
    conversationId: (nextItem as MedRequest)?.latestFanConversationId ?? '',
    enabled: !isInvestigation && !!(nextItem as MedRequest)?.latestFanConversationId
  })

  useGetConversationById({
    conversationId: (nextItem as MedRequest)?.rejectionConversationId ?? '',
    enabled: !isInvestigation && !!(nextItem as MedRequest)?.rejectionConversationId
  })

  const lockMedRequest = !isInvestigation
    ? medRequest?.data
      ? shouldLockOpenedMedRequest(medRequest.data)
      : false
    : true
  const lockInvestigation = isInvestigation && investigation?.data ? shouldLockInvestigation() : false

  useLockItemById(
    nextItem?.id ?? '',
    !!nextItem && enabled && (isInvestigation ? lockInvestigation : lockMedRequest),
    isInvestigation
  )
  usePatientOrderHistory(nextItem?.patient?.id ?? '', !!nextItem?.patient.id && enabled)
  useConsultationAISummary(!isInvestigation ? (nextItem as MedRequest) : undefined)
  useGetMedications()

  useScrAiSummary({
    medicationRequestId: nextItem?.id ?? '',
    enabled: !isInvestigation
  })

  return nextItem
}

export default usePrepareNextRequest
